import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api, { uploadPhoto, getEvidenceDisplayUrl } from '../lib/api';
import { formatDisplayDateTime } from '../lib/dateUtils';
import toast from 'react-hot-toast';
import Button from '../components/ui/Button';
import Select from '../components/ui/Select';
import Textarea from '../components/ui/Textarea';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';

const MAX_IMAGES = 5;
const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'bug', label: 'Bug' },
  { value: 'suggestion', label: 'Suggestion' },
  { value: 'feature', label: 'Feature Request' },
];

export default function DevNotes() {
  const queryClient = useQueryClient();
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('general');
  const [imageUrls, setImageUrls] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [replyingToId, setReplyingToId] = useState(null);
  const [draftComment, setDraftComment] = useState('');

  const { data: feedbackList = [], isLoading, error } = useQuery(
    ['feedback'],
    () => api.get('/feedback').then((res) => res.data),
    { staleTime: 30_000 }
  );

  const createMutation = useMutation(
    (payload) => api.post('/feedback', payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['feedback']);
        setContent('');
        setImageUrls([]);
        toast.success('Feedback submitted');
      },
      onError: (err) => {
        toast.error(err.response?.data?.error || 'Failed to submit feedback');
      },
    }
  );

  const updateStatusMutation = useMutation(
    ({ id, status }) => api.patch(`/feedback/${id}`, { status }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['feedback']);
        toast.success('Status updated');
      },
      onError: (err) => {
        toast.error(err.response?.data?.error || 'Failed to update status');
      },
    }
  );

  const addCommentMutation = useMutation(
    ({ feedbackId, content }) => api.post(`/feedback/${feedbackId}/comments`, { content }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['feedback']);
        setReplyingToId(null);
        setDraftComment('');
        toast.success('Comment added');
      },
      onError: (err) => {
        toast.error(err.response?.data?.error || 'Failed to add comment');
      },
    }
  );

  const handleFileChange = useCallback(async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    const remaining = MAX_IMAGES - imageUrls.length;
    if (remaining <= 0) {
      toast.error(`Maximum ${MAX_IMAGES} images per note`);
      e.target.value = '';
      return;
    }
    const toUpload = Array.from(files).slice(0, remaining);
    setUploading(true);
    try {
      const urls = await Promise.all(toUpload.map((f) => uploadPhoto(f)));
      setImageUrls((prev) => [...prev, ...urls].slice(0, MAX_IMAGES));
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }, [imageUrls.length]);

  const removeImage = useCallback((index) => {
    setImageUrls((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmed = content.trim();
    if (!trimmed) {
      toast.error('Please enter your feedback');
      return;
    }
    createMutation.mutate({ content: trimmed, category, image_urls: imageUrls });
  };

  return (
    <div className="page">
      <header className="mb-4">
        <h1 className="page-title">Dev Notes</h1>
        <p className="text-muted">
          Share suggestions, report bugs, or request features. Visible to all team members.
        </p>
      </header>

      <Card style={{ marginBottom: '2rem' }}>
        <h2 className="card-title">Submit feedback</h2>
        <form onSubmit={handleSubmit}>
          <Select
            id="dev-notes-category"
            label="Category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </Select>
          <Textarea
            id="dev-notes-content"
            label="Your feedback *"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={4}
            placeholder="Describe your suggestion, bug, or feature request..."
            required
          />
          <div className="form-group">
            <label htmlFor="dev-notes-images" className="form-label">Images (optional, max {MAX_IMAGES})</label>
            <input
              id="dev-notes-images"
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              disabled={uploading || imageUrls.length >= MAX_IMAGES}
              className="form-input"
            />
            {uploading && <span className="text-muted" style={{ fontSize: '0.85rem' }}>Uploading…</span>}
            {imageUrls.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                {imageUrls.map((url, i) => (
                  <div key={url} style={{ position: 'relative', flexShrink: 0 }}>
                    <img
                      src={getEvidenceDisplayUrl(url)}
                      alt={`Attachment ${i + 1}`}
                      style={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 6 }}
                    />
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => removeImage(i)}
                      style={{
                        position: 'absolute',
                        top: 2,
                        right: 2,
                        padding: '2px 6px',
                        fontSize: '0.75rem',
                        minHeight: 'auto',
                      }}
                      aria-label="Remove image"
                    >
                      ×
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="form-actions">
            <Button
              type="submit"
              variant="primary"
              disabled={createMutation.isLoading || uploading || !content.trim()}
            >
              {createMutation.isLoading ? 'Submitting…' : 'Submit'}
            </Button>
          </div>
        </form>
      </Card>

      <Card>
        <h2 className="card-title">Recent feedback</h2>
        {isLoading ? (
          <p className="text-muted">Loading…</p>
        ) : error ? (
          <p className="text-muted">Error: {error.message}</p>
        ) : feedbackList.length === 0 ? (
          <p className="text-muted">No feedback yet. Be the first to share!</p>
        ) : (
          <ul className="dev-notes-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {feedbackList.map((item) => (
              <li
                key={item.id}
                className={`dev-notes-item${item.status === 'addressed' ? ' addressed' : ''}`}
                style={{
                  padding: '1rem 0',
                  borderBottom: '1px solid var(--border-subtle)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.4rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <Badge>{item.category}</Badge>
                    <Badge variant={item.status === 'addressed' ? 'success' : 'muted'} style={{ fontSize: '0.75rem' }}>
                      {item.status === 'addressed' ? 'Addressed' : 'Open'}
                    </Badge>
                  </div>
                  <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                    {item.userFirstName || item.userLastName ? `${item.userFirstName || ''} ${item.userLastName || ''}`.trim() : item.userEmail || 'Anonymous'} • {formatDisplayDateTime(item.createdAt)}
                  </span>
                </div>
                <p className="dev-notes-item-content" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{item.content}</p>
                {(item.imageUrls || []).length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                    {(item.imageUrls || []).map((url, i) => (
                      <a
                        key={url}
                        href={getEvidenceDisplayUrl(url)}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ display: 'block' }}
                      >
                        <img
                          src={getEvidenceDisplayUrl(url)}
                          alt={`Attachment ${i + 1}`}
                          style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6 }}
                        />
                      </a>
                    ))}
                  </div>
                )}
                <div style={{ marginTop: '0.75rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
                  <Button
                    type="button"
                    size="sm"
                    disabled={updateStatusMutation.isLoading}
                    onClick={() => updateStatusMutation.mutate({ id: item.id, status: item.status === 'addressed' ? 'open' : 'addressed' })}
                  >
                    {item.status === 'addressed' ? 'Reopen' : 'Mark addressed'}
                  </Button>
                </div>
                {(item.comments || []).length > 0 && (
                  <div style={{ marginTop: '0.75rem', paddingLeft: '0.5rem', borderLeft: '3px solid var(--border-subtle)' }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.35rem' }}>Comments</div>
                    {(item.comments || []).map((c) => (
                      <div key={c.id} style={{ marginBottom: '0.5rem', fontSize: '0.9rem' }}>
                        <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                          {c.userFirstName || c.userLastName ? `${c.userFirstName || ''} ${c.userLastName || ''}`.trim() : c.userEmail || 'Anonymous'} • {formatDisplayDateTime(c.createdAt)}
                        </span>
                        <p style={{ margin: '0.2rem 0 0', whiteSpace: 'pre-wrap' }}>{c.content}</p>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{ marginTop: '0.5rem' }}>
                  {replyingToId === item.id ? (
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        const trimmed = draftComment.trim();
                        if (!trimmed) return;
                        addCommentMutation.mutate({ feedbackId: item.id, content: trimmed });
                      }}
                      style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
                    >
                      <Textarea
                        id={`comment-${item.id}`}
                        value={draftComment}
                        onChange={(e) => setDraftComment(e.target.value)}
                        placeholder="Add a comment..."
                        rows={2}
                        maxLength={2000}
                      />
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <Button type="submit" variant="primary" size="sm" disabled={!draftComment.trim() || addCommentMutation.isLoading}>
                          {addCommentMutation.isLoading ? 'Adding…' : 'Add comment'}
                        </Button>
                        <Button type="button" size="sm" onClick={() => { setReplyingToId(null); setDraftComment(''); }}>
                          Cancel
                        </Button>
                      </div>
                    </form>
                  ) : (
                    <Button type="button" size="sm" onClick={() => setReplyingToId(item.id)}>
                      Add comment
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
